import React from "react";
import { Helmet } from "react-helmet";
import HomepageHero from "@webcore/components/domain/content-site/sections/HomepageHero";
import LogosStrip from "@webcore/components/domain/content-site/sections/LogosStrip";
import ThesisSection from "@webcore/components/domain/content-site/sections/ThesisSection";
import PrinciplesSection, { Principle } from "@webcore/components/domain/content-site/sections/PrinciplesSection";
import WorkflowsSection, { WorkflowCardData } from "@webcore/components/domain/content-site/sections/WorkflowsSection";
import ArchitectureSection from "@webcore/components/domain/content-site/sections/ArchitectureSection";
import CalloutSection from "@webcore/components/domain/content-site/sections/CalloutSection";
import CtaSection from "@webcore/components/domain/content-site/sections/CtaSection";
import SpotlightSection from "@webcore/components/domain/content-site/sections/SpotlightSection";
import QuoteSection from "@webcore/components/domain/content-site/sections/QuoteSection";

import pipelineReviewImg from "@/assets/workflows/pipeline-review.jpg";
import mrrCloseImg from "@/assets/workflows/mrr-close.jpg";
import commissionRunImg from "@/assets/workflows/commission-run.jpg";
import varianceReportingImg from "@/assets/workflows/variance-reporting.jpg";

const connectorLogos = [
  { label: "Maxio" },
  { label: "Salesforce", style: "serif" as const },
  { label: "HubSpot", style: "italic" as const },
  { label: "Stripe" },
  { label: "Shopify", style: "mono" as const },
  { label: "Amazon" },
  { label: "NetSuite", style: "italic" as const },
  { label: "QuickBooks" },
];

const principles: Principle[] = [
  {
    num: "01",
    kicker: "unify",
    title: <>One auditable data model across <em>your whole stack.</em></>,
    body: (
      <>
        Billing, CRM, accounting, payments, ecommerce. Surfaced as{" "}
        <strong>typed business objects</strong> — customers, deals, subscriptions, revenue events,
        monthly rollforwards — that your team can query directly. The work you'd otherwise piece
        together with Fivetran, dbt, and a BI tool, replaced with one platform.
      </>
    ),
  },
  {
    num: "02",
    kicker: "defend",
    title: <>Every number traces to a <em>source record.</em></>,
    body: (
      <>
        Every transformation is <strong>versioned SQL you can read, review, and change</strong>.
        Every override leaves a note. When finance asks "where did this number come from?" — the
        answer is two clicks away, not a three-day reconciliation project.
      </>
    ),
  },
  {
    num: "03",
    kicker: "run",
    title: <>Purpose-built workflows for the jobs that <em>consume your team's time.</em></>,
    body: (
      <>
        Pipeline review, MRR close, commission calculation, variance reporting, exception triage.{" "}
        <strong>Automated where automation is safe, surfaced for human judgment where it isn't</strong>,
        and built on the same data model your dashboards run on. The numbers in the workflow are
        the same numbers in the report.
      </>
    ),
  },
];

const workflows: WorkflowCardData[] = [
  {
    num: "// 01",
    title: "Pipeline Review",
    sub: "Stop running forecast meetings on stale Salesforce reports.",
    body: (
      <>
        Weighted pipeline by stage and product. <strong>Zombie deal detection</strong> — stale, $0,
        or no-recent-activity deals flagged automatically. Forecast vs. actual accuracy tracked at
        the rep and team level. Drill from any tile into the underlying opportunities, owners, and
        source records.
      </>
    ),
    screenshot: pipelineReviewImg,
    replacesLabel: "Replaces or sits alongside",
    replacesItems: "Salesforce reports · Clari · forecast spreadsheets",
  },
  {
    num: "// 02",
    title: "MRR Close",
    sub: "The monthly rollforward your finance team rebuilds in a spreadsheet — built once, run forever.",
    body: (
      <>
        Monthly MRR movement classified into new logo, cross-sell, upsell, downgrade, and churn.{" "}
        <strong>Customer-level audit panel</strong> for every cell. Override workflow for the
        judgment calls that don't fit the rules. Projections from contracted future revenue when
        actuals haven't billed yet.
      </>
    ),
    screenshot: mrrCloseImg,
    replacesLabel: "Replaces",
    replacesItems: "the golden-truth spreadsheet · the analyst who maintains it · the three-day month-end cycle",
  },
  {
    num: "// 03",
    title: "Commission Run",
    sub: "Commission rules as versioned code, not a comp spreadsheet nobody trusts.",
    body: (
      <>
        Per-rep monthly statements. New deals, prior-month deals with current MRR deltas applied to
        rates, terminated deals zeroed automatically. <strong>Bonus tiers, accelerators, and
        clawback rules live as readable SQL</strong> — not buried in a vendor's UI.
      </>
    ),
    screenshot: commissionRunImg,
    replacesLabel: "Replaces",
    replacesItems: "CaptivateIQ · QuotaPath · your homegrown comp sheet · the comp disputes",
  },
  {
    num: "// 04",
    title: "Variance Reporting",
    sub: "Budget vs. actuals, defended.",
    body: (
      <>
        Monthly budget compared against billed revenue and tracked expenses, with{" "}
        <strong>forward-looking projections from contracted revenue</strong>. Variance attribution
        down to the line item. Drill from any cell to the invoices, contracts, or expense records
        that produced it.
      </>
    ),
    screenshot: varianceReportingImg,
    replacesLabel: "Replaces",
    replacesItems: "the FP&A team's monthly export-and-pivot ritual",
  },
];

const architectureSources = [
  "Maxio", "Salesforce", "HubSpot", "Stripe", "Shopify", "Amazon", "NetSuite", "QuickBooks",
];

const Homepage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Data FabrIQ — Operational software for your business</title>
        <meta
          name="description"
          content="Operational software for your business. Built on a data layer that defends itself. Pipeline reviews, MRR closes, commission runs, and variance reports — on numbers you can audit to the row."
        />
      </Helmet>

      <HomepageHero />

      <LogosStrip entries={connectorLogos} />

      <ThesisSection
        id="platform"
        label="// our thesis"
        heading={
          <>
            Most BI tools ask you to <span className="strike">trust the data.</span><br />
            We assume <span className="accent">you shouldn't have to.</span>
          </>
        }
        body={
          <>
            <p>
              The numbers on a finance, revops, or marketing dashboard are the output of{" "}
              <strong>dozens of decisions</strong> — which records to include, how to classify a
              movement, what to do when Maxio and Salesforce disagree, whose rep ID gets the
              credit, whether yesterday's Amazon order ties back to last month's ad spend. Most
              tools bury those decisions in vendor UI. Change them and last quarter's numbers
              change with them.
            </p>
            <p>
              Data FabrIQ flips the default. Business logic is{" "}
              <strong>SQL you can read and version</strong>. Every cell traces back to a source
              record. And on top of that auditable layer, we ship the{" "}
              <strong>workflows your team actually runs the business with</strong> — pipeline
              reviews, MRR closes, commission calculations, variance reports, marketing
              attribution, customer lifetime value — purpose-built, AI-assisted, and{" "}
              <strong>auditable to the row</strong>.
            </p>
          </>
        }
      />

      <PrinciplesSection
        eyebrow="// what it does"
        heading={
          <>
            Three things <span className="serif">that matter</span><br />
            more than <span className="dim">another chart type.</span>
          </>
        }
        intro={
          <>
            Every business we've worked with has the same problem: a billing system, a CRM, an
            accounting system, a golden-truth spreadsheet that reconciles them, and a team
            spending hours a week maintaining the arithmetic between them. Here's what replaces
            that.
          </>
        }
        principles={principles}
      />

      <WorkflowsSection
        id="workflows"
        eyebrow="// what your team stops doing in spreadsheets"
        heading={
          <>
            The operational software you'd build<br />
            if you had time. <span className="serif">Already built.</span>
          </>
        }
        intro={
          <>
            Every workflow runs on the same auditable data model. Every number is drillable. Every
            override leaves a trail. These aren't dashboards with extra buttons — they're the apps
            your team actually opens to get the work done.
          </>
        }
        workflows={workflows}
      />

      <ArchitectureSection
        id="how-it-works"
        eyebrow="// how it works"
        heading={
          <>
            One platform.<br />
            Two surfaces.<br />
            <span className="serif">A foundation you can audit.</span>
          </>
        }
        intro={
          <>
            Source systems land in a bronze layer of raw, untouched data. Transformation specs —
            queries and rules to dedupe, merge, optimize and clean, written by your team or ours —
            produce a typed gold model of business objects. KPIs, dashboards, and workflows all
            read from that same model, so the number on a Monday pipeline review is the same
            number on the quarterly board deck. Every cell knows where it came from.
          </>
        }
        sources={architectureSources}
        foot="Every transformation versioned · every cell drillable · every override traceable"
      />

      <CalloutSection
        quote={
          <>
            The dashboard is the <em>last mile.</em> The product is everything{" "}
            <em>underneath it</em> — and the software you build on top of it when a chart is not
            enough.
          </>
        }
        cite="— Data FabrIQ engineering principle · №01"
      />

      <CtaSection
        heading={
          <>
            Numbers you can <span className="serif">actually defend.</span><br />
            Workflows your team <span className="serif">actually uses.</span>
          </>
        }
        body="See your own source systems unified, audited, and running real workflows inside a week."
      />

      <SpotlightSection
        id="amazon-sellers"
        eyebrow="// amazon sellers"
        heading={<>Built for <span className="serif">Amazon sellers,</span> too.</>}
        intro={
          <>
            Connect Amazon Seller Central through Amazon&apos;s own Login with Amazon flow and see
            your marketplace performance alongside the rest of your business.
          </>
        }
        bullets={[
          <>
            <strong>The reports that matter, ingested automatically.</strong> Aggregated Selling
            Partner API reports — sales &amp; traffic, Brand Analytics search terms, promotion
            performance, and seller economics — land in your unified operations dashboard.
          </>,
          <>
            <strong>Joined with the rest of your stack.</strong> Amazon data unifies with your
            advertising, accounting, and CRM sources for true return on ad spend, SKU-level
            margin, and channel contribution.
          </>,
          <>
            <strong>Read-only by consent.</strong> Connect through Amazon&apos;s standard Login
            with Amazon (LWA) OAuth flow with read-only Brand Analytics access — revocable any
            time, from DataFabrIQ or from Amazon.
          </>,
        ]}
        linkLabel="Full Amazon Seller connector details →"
        linkHref="https://app.datafabriq.co/public-content/amazon-seller-app"
      />

      <QuoteSection
        id="pricing"
        eyebrow="// pricing"
        heading={
          <>
            Contact Sales to get started
          </>
        }
        intro={
          <>
            <p>
              DataFabrIQ is offered as a platform subscription. Pricing is quote-based and
              depends on the number of source systems you connect, your data volume, and the size
              of your team. Connectors — including the Amazon Seller Central connector — are
              included in the subscription: <strong>no separate connector fees, usage surcharges,
              or hidden service fees.</strong>
            </p>
            <p>
              Tell us a little about your business and our team will follow up with a detailed
              quote, usually within one business day.
            </p>
          </>
        }
      />
    </>
  );
};

export default Homepage;
